import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

const ROLES = {
  head: 'Head',
  assistant: 'Assistant',
  cit: 'CIT',
  helper: 'Helper'
};

export function generatePDF({ date, fullDate, groups, getAttendance, counselors }) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Colors
  const navyColor = [0, 85, 164];
  const goldColor = [240, 180, 41];
  const grayColor = [245, 245, 245];
  
  let yPos = 15;

  // Header bar
  doc.setFillColor(...navyColor);
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  // Camp name
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Rolling River Day Camp', 15, 15);
  
  // Report title
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text('Holiday Camp Attendance Report', 15, 25);
  
  // Date
  doc.setFontSize(12);
  doc.text(fullDate, pageWidth - 15, 15, { align: 'right' });
  
  // Generated timestamp
  const now = new Date();
  const timestamp = `Generated: ${now.toLocaleDateString()} at ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  doc.setFontSize(10);
  doc.text(timestamp, pageWidth - 15, 25, { align: 'right' });
  
  yPos = 45;

  // Staff Section
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Staff', 15, yPos);
  yPos += 5;

  if (counselors && counselors.length > 0) {
    const staffData = counselors.map(c => [c.name, ROLES[c.role] || c.role]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Name', 'Role']],
      body: staffData,
      margin: { left: 15, right: 15 },
      headStyles: {
        fillColor: navyColor,
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: grayColor
      },
      styles: {
        fontSize: 10,
        cellPadding: 4
      }
    });
    
    yPos = doc.lastAutoTable.finalY + 15;
  } else {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('No staff assigned', 15, yPos + 5);
    yPos += 15;
  }

  // Attendance Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Attendance by Group', 15, yPos);
  yPos += 5;

  // Track totals
  let totalPresent = 0;
  let totalLate = 0;
  let totalLeaving = 0;
  let totalAbsent = 0;
  let totalNotSelected = 0;
  let grandTotal = 0;

  // Sort groups
  const groupNames = Object.keys(groups).sort((a, b) => {
    if (a === 'Unassigned') return 1;
    if (b === 'Unassigned') return -1;
    return a.localeCompare(b);
  });

  groupNames.forEach((groupName, groupIndex) => {
    const campers = groups[groupName];
    
    // Check if we need a new page
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    // Group header
    doc.setFillColor(...navyColor);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.rect(15, yPos, pageWidth - 30, 8, 'F');
    doc.text(groupName, 18, yPos + 6);
    yPos += 12;

    // Group stats
    let groupPresent = 0;
    let groupLate = 0;
    let groupLeaving = 0;
    let groupAbsent = 0;
    let groupNotSelected = 0;

    // Camper data
    const camperData = campers.map((camper, index) => {
      const attendance = getAttendance(camper.id, date);
      const firstName = camper.firstName || 'Unknown';
      const lastName = camper.lastName || '';
      
      let status = 'NOT SELECTED';
      let notes = [];
      
      if (attendance.status === 'not_selected') {
        status = 'NOT SELECTED';
        groupNotSelected++;
      } else if (attendance.status === 'absent') {
        status = 'Absent';
        groupAbsent++;
      } else {
        groupPresent++;
        
        if (attendance.lateTime) {
          status = 'Late';
          notes.push(`Arrived ${attendance.lateTime}`);
          groupLate++;
        }
        
        if (attendance.earlyTime) {
          if (status === 'Late') {
            status = 'Late/Leaving';
          } else {
            status = 'Leaving Early';
          }
          notes.push(`Dismissed ${attendance.earlyTime}`);
          groupLeaving++;
        }

        if (status === 'NOT SELECTED') {
          status = 'Present';
        }
      }
      
      return [index + 1, `${firstName} ${lastName}`, status, notes.join('\n')];
    });

    autoTable(doc, {
      startY: yPos,
      head: [['#', 'Camper Name', 'Status', 'Notes']],
      body: camperData,
      margin: { left: 15, right: 15 },
      headStyles: {
        fillColor: [100, 116, 139],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9
      },
      alternateRowStyles: {
        fillColor: grayColor
      },
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 50 },
        2: { cellWidth: 30 },
        3: { cellWidth: 'auto' }
      }
    });

    yPos = doc.lastAutoTable.finalY + 3;

    // Group subtotal
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text(
      `${groupPresent} Present, ${groupLate} Late, ${groupLeaving} Leaving Early, ${groupAbsent} Absent${groupNotSelected > 0 ? `, ${groupNotSelected} NOT SELECTED` : ''}`,
      pageWidth - 15,
      yPos,
      { align: 'right' }
    );
    yPos += 10;

    // Update totals
    totalPresent += groupPresent;
    totalLate += groupLate;
    totalLeaving += groupLeaving;
    totalAbsent += groupAbsent;
    totalNotSelected += groupNotSelected;
    grandTotal += campers.length;
  });

  // Check if we need a new page for footer
  if (yPos > 260) {
    doc.addPage();
    yPos = 20;
  }

  // Daily totals footer
  yPos += 5;
  doc.setFillColor(...navyColor);
  doc.rect(15, yPos, pageWidth - 30, 20, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Daily Totals:', 20, yPos + 8);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Present: ${totalPresent}  |  Late: ${totalLate}  |  Leaving Early: ${totalLeaving}  |  Absent: ${totalAbsent}${totalNotSelected > 0 ? `  |  NOT SELECTED: ${totalNotSelected}` : ''}  |  Grand Total: ${grandTotal}`,
    20,
    yPos + 15
  );

  // Page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  // Save the PDF
  const fileName = `RRDC_Attendance_${date}.pdf`;
  doc.save(fileName);
}
